import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  IS_NATIVE,
  getBiometricAvailability,
  hasOptedIn,
  isArmedFor,
  wasPrompted,
  markPrompted,
  enrollBiometricSignIn,
  rearmBiometricSignInIfNeeded,
} from "../lib/biometricSignIn";
// Same approved look as the "Notifications are off" popup (emoji / title / one
// line / green CTA / ghost "Not now") — one visual language for every
// permission-ish moment.
import { onQuietRoute } from "../lib/firstSignInMoment";
import "./NotificationsOffPopup.css";
import { t } from "../i18n";

// Founder ask: "after they sign in for the very first time —
// 'Would you like to set up Face ID?'". Mounted once in App (native only):
//   • first authenticated landing for THIS account on THIS install → one
//     prompt, remembered forever (per account, so a shared phone asks each
//     talent once). Signup, Google, Apple, email — every door lands here.
//   • already opted in but the install isn't armed for this account (key
//     expired / revoked / another account was armed / first launch after
//     the update) → silent re-arm, no UI.
// Held off on the quiet routes (auth, onboarding / Stripe return, the
// first-run tour — lib/firstSignInMoment) so it never stacks on top of them,
// and delayed a beat so the Requests page settles first. Apple's notification
// prompt comes AFTER this question (build 38 order, same lib).
const SETTLE_DELAY_MS = 1500;

const BiometricEnrollPrompt: React.FC = () => {
  const { user, isAuthenticated, loading } = useContext(AuthContext);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("Face ID");
  const [busy, setBusy] = useState(false);
  const timerRef = useRef<number | null>(null);

  const userId = user?._id;
  const path = location.pathname;

  useEffect(() => {
    if (!IS_NATIVE || loading || !isAuthenticated || !userId) return;
    if (onQuietRoute(path)) return;
    let cancelled = false;

    (async () => {
      const { available, label: l } = await getBiometricAvailability();
      if (cancelled || !available) return;
      setLabel(l);

      if (hasOptedIn(userId)) {
        // Opted in earlier — keep this install armed for this account, quietly.
        if (!isArmedFor(userId)) rearmBiometricSignInIfNeeded(user);
        return;
      }
      if (wasPrompted(userId)) return;

      timerRef.current = window.setTimeout(() => {
        if (!cancelled) setOpen(true);
      }, SETTLE_DELAY_MS);
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // `user` is intentionally read at effect time only (identity by _id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading, userId, path]);

  if (!IS_NATIVE || !open || !user) return null;

  const dismiss = () => {
    markPrompted(user._id);
    setOpen(false);
  };

  const turnOn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await enrollBiometricSignIn(user, { verifyFirst: true });
      markPrompted(user._id);
      setOpen(false);
    } catch {
      // Backed out of the Face ID sheet, or the key couldn't be issued → treat
      // as "Not now" (asked once, no nagging). The Preferences switch is
      // always there to turn it on later.
      dismiss();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="notifoff-overlay" role="dialog" aria-modal="true" aria-labelledby="bio-prompt-title">
      <div className="notifoff-card">
        <div className="notifoff-emoji" aria-hidden="true">
          🔐
        </div>
        <div className="notifoff-title" id="bio-prompt-title">
          {t("biometricEnrollPrompt.setUp", { label })}
        </div>
        <p className="notifoff-sub">{t("biometricEnrollPrompt.theFastestWayBack")}</p>
        <button type="button" className="notifoff-cta" onClick={turnOn} disabled={busy}>
          {busy ? t("biometricEnrollPrompt.settingUp") : t("biometricEnrollPrompt.setUp2", { label })}
        </button>
        <button type="button" className="notifoff-ghost" onClick={dismiss} disabled={busy}>
          {t("biometricEnrollPrompt.notNow")}
        </button>
      </div>
    </div>
  );
};

export default BiometricEnrollPrompt;
